import { ApplicationRef, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { sleep } from '@cf-shared/shared-util';
import { AnimationController } from 'providers/animation-controller';
import { Loading, LoadingController } from 'providers/loading-controller';
import { PopoverController } from 'providers/popover-controller';
import { DestroyGuard } from 'services/browser-utils';

@Component({
  selector: 'loading-host',
  templateUrl: 'loading-host.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoadingHostComponent implements OnDestroy {
  private destroyGuard = new DestroyGuard();
  loading: Loading;
  @ViewChild('popupElement') popupElement: ElementRef;
  @ViewChild('cover') coverElement: ElementRef;

  constructor(
    public elementRef: ElementRef,
    private loadingController: LoadingController,
    private popoverController: PopoverController,
    private appRef: ApplicationRef,
    private cdr: ChangeDetectorRef,
    private animationController: AnimationController
  ) {
    this.loadingController.registerHost(this);
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
  }

  async setLoading(loading: Loading) {
    if (!loading || !this.loading) {
      let element = this.elementRef.nativeElement as HTMLElement;

      if (loading) {
        this.loading = loading;
        element.style.setProperty('display', 'block');

        // Calculate z-index to be above all current popovers
        let maxZIndex = 1002; // Base z-index (same as popover default)

        // Check if PopoverController has active popovers
        if (this.popoverController.popoverHost && this.popoverController.popoverHost.popovers) {
          const activePopovers = this.popoverController.popoverHost.popovers.length;
          if (activePopovers > 0) {
            // Each popover layer adds to the z-index
            maxZIndex = 1002 + activePopovers;
          }
        }

        // Set loading z-index to be above the highest popover
        element.style.setProperty('z-index', String(maxZIndex + 1));
      }

      this.appRef.tick();
      if (!this.destroyGuard.isDestroyed) {
        this.cdr.markForCheck();
      }
      await sleep(0); // wait for the DOM to update

      const coverElement = this.coverElement.nativeElement;
      const popupElement = this.popupElement.nativeElement;

      const coverAnimation = coverElement.animate(
        loading
          ? [
            { background: 'transparent', filter: 'blur(0px)' },
            { background: 'rgba(0, 0, 0, 0.5)', filter: 'blur(1px)' },
          ]
          : [
            { background: 'rgba(0, 0, 0, 0.5)', filter: 'blur(1px)' },
            { background: 'transparent', filter: 'blur(0px)' },
          ],
        {
          duration: 200, // duration in milliseconds
          fill: 'forwards', // ensures the animation stays at the end state
          easing: 'ease-out', // easing function
        }
      );

      const popupAnimation = popupElement.animate(
        loading
          ? [
            { scale: '0 0' },
            { scale: '1 1' },
          ]
          : [
            { scale: '1 1' },
            { scale: '0 0' },
          ],
        {
          duration: 200, // duration in milliseconds
          fill: 'forwards', // ensures the animation stays at the end state
          easing: 'ease-out', // easing function
        }
      );

      // Wait for both animations to finish
      await this.animationController.safeAwaitAnimations([coverAnimation, popupAnimation]);

      if (!loading) {
        element.style.removeProperty('display');
        element.style.removeProperty('z-index');
      }
    } else {
      this.loading = loading;
    }

    if (!loading) {
      this.loading = undefined;
    }

    this.appRef.tick();
    if (!this.destroyGuard.isDestroyed) {
      this.cdr.markForCheck();
    }
  }

  getMessage() {
    if (this.loading && this.loading.opts) {
      return this.loading.opts.content;
    }
    return undefined;
  }
}

